import { useMemo, useState } from 'react';
import { makeStyles, tokens, Text } from '@fluentui/react-components';
import {
  architectureNodes,
  architectureEdges,
  TOPOLOGY_VIEW_W,
  TOPOLOGY_VIEW_H,
  type TopologyNode,
} from '../lib/architectureTopology';
import type { ArchitectureNodeStat } from '../api/architecture';

// Workbench MVP Session A — hand-rolled SVG topology per project convention
// (see TrendsChart.tsx for the pattern).
//
// * Normalized 100 × 60 viewBox so `width: 100%` scales the diagram to the
//   container. `vector-effect: non-scaling-stroke` keeps edge weight crisp at
//   any container width.
// * Fills key off node status ("healthy" / "degraded" / "idle"). A node in the
//   topology with no matching stat renders as idle.
// * Tooltip is an HTML div positioned in percent — richer than SVG `<title>`
//   but the `<title>` stays for screen readers.

interface Props {
  stats: ArchitectureNodeStat[];
  height?: number;    // px, defaults to 320
}

interface HoverState {
  node: TopologyNode;
  stat: ArchitectureNodeStat | null;
  xPct: number;
  yPct: number;
}

const NODE_R = 4.5;   // node radius in viewBox units

const useStyles = makeStyles({
  root: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
    marginBottom: '16px',
    position: 'relative',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  legend: {
    display: 'flex',
    gap: '14px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  swatch: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  chartWrap: {
    position: 'relative',
    width: '100%',
  },
  tooltip: {
    position: 'absolute',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '6px',
    boxShadow: tokens.shadow8,
    padding: '10px 12px',
    fontSize: '12px',
    lineHeight: 1.4,
    pointerEvents: 'none',
    transform: 'translate(-50%, -100%)',
    marginTop: '-10px',
    whiteSpace: 'nowrap' as const,
    zIndex: 2,
  },
  tooltipTitle: {
    fontWeight: 600,
    marginBottom: '4px',
  },
  tooltipRow: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums' as const,
  },
});

function fillFor(status: string | undefined): string {
  if (status === 'healthy') return '#3fa66a';
  if (status === 'degraded') return '#e39d3b';
  return tokens.colorNeutralStroke2;   // idle / unknown
}

function fmtMs(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${v.toFixed(0)}ms`;
}

export function ArchitectureFlowDiagram({ stats, height = 320 }: Props) {
  const s = useStyles();
  const [hover, setHover] = useState<HoverState | null>(null);

  const statsByNode = useMemo(() => {
    const m = new Map<string, ArchitectureNodeStat>();
    for (const stat of stats) m.set(stat.nodeId, stat);
    return m;
  }, [stats]);

  return (
    <div className={s.root}>
      <div className={s.header}>
        <Text weight="semibold">Runtime topology</Text>
        <div className={s.legend}>
          <span className={s.legendItem}>
            <span className={s.swatch} style={{ background: '#3fa66a' }} /> Healthy
          </span>
          <span className={s.legendItem}>
            <span className={s.swatch} style={{ background: '#e39d3b' }} /> Degraded
          </span>
          <span className={s.legendItem}>
            <span className={s.swatch} style={{ background: tokens.colorNeutralStroke2 }} /> Idle
          </span>
        </div>
      </div>

      <div className={s.chartWrap}>
        <svg
          viewBox={`0 0 ${TOPOLOGY_VIEW_W} ${TOPOLOGY_VIEW_H}`}
          role="img"
          aria-label="Architecture topology"
          style={{ width: '100%', height, display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* edges — drawn first so nodes overlap their endpoints */}
          {architectureEdges.map((edge, i) => {
            const from = architectureNodes.find((n) => n.id === edge.from);
            const to = architectureNodes.find((n) => n.id === edge.to);
            if (!from || !to) return null;
            return (
              <line
                key={i}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={tokens.colorNeutralStroke2}
                strokeWidth={0.6}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          {/* nodes */}
          {architectureNodes.map((n) => {
            const stat = statsByNode.get(n.id) ?? null;
            const fill = fillFor(stat?.status);
            return (
              <g
                key={n.id}
                onMouseEnter={() => setHover({
                  node: n,
                  stat,
                  xPct: n.x / TOPOLOGY_VIEW_W,
                  yPct: n.y / TOPOLOGY_VIEW_H,
                })}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={n.x}
                  cy={n.y}
                  r={NODE_R}
                  fill={fill}
                  stroke={tokens.colorNeutralForeground3}
                  strokeWidth={0.4}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={n.x}
                  y={n.y + NODE_R + 3.5}
                  textAnchor="middle"
                  fontSize={2.8}
                  fill={tokens.colorNeutralForeground1}
                >
                  {n.label}
                </text>
                <title>{n.label} — {stat?.status ?? 'idle'}</title>
              </g>
            );
          })}
        </svg>

        {hover && (
          <div
            className={s.tooltip}
            style={{ left: `${hover.xPct * 100}%`, top: `${hover.yPct * 100}%` }}
          >
            <div className={s.tooltipTitle}>{hover.node.label}</div>
            <div className={s.tooltipRow}>
              status: <b>{hover.stat?.status ?? 'idle'}</b>
            </div>
            <div className={s.tooltipRow}>
              invocations: {hover.stat?.invocationCount ?? 0}
            </div>
            <div className={s.tooltipRow}>
              avg: {fmtMs(hover.stat?.avgLatencyMs ?? null)} · p95: {fmtMs(hover.stat?.p95LatencyMs ?? null)}
            </div>
            <div className={s.tooltipRow}>
              errors: {hover.stat?.errorCount ?? 0} · SLA breaches: {hover.stat?.slaBreachCount ?? 0}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
